import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  keepPreviousData,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { BookingQueryKeys } from '../queryKeys';
import { IRange, TDeskOrParkingResourceTypeDto } from '@jooxter/utils';
import {
  BookingCapabilities,
  BookingCompressed,
  BookingService,
  IRemainingBookingsCheckOptions,
  ISearchBookingsParameters,
  JxtBookingStatusEnum,
  User,
  WorkplaceRuleRemainingDays,
} from '@jooxter/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookingStaleTimeEnum } from '../staleTimes';
import { flatMap, uniqBy } from 'lodash-es';

const MAX_BATCH_DAYS = 44; // Maximum number of days per batch for API requests. Adjust this value based on API limitations or performance considerations.

export const useSearchOneBookingForUsers = (userIds: number[], range: IRange, enabled: boolean) => {
  const options = useMemo(
    () => ({
      ...range,
      size: 1,
      status: [JxtBookingStatusEnum.APPROVED, JxtBookingStatusEnum.REQUESTED, JxtBookingStatusEnum.FINISHED],
    }),
    [range]
  );
  const { data: bookings, isPending } = useQueries({
    queries:
      userIds?.map((id: number) => ({
        queryKey: [BookingQueryKeys.GetOneBookingForUser, id, options],
        queryFn: () => BookingService.search({ participantId: id, ...options }),
        placeholderData: keepPreviousData,
        staleTime: BookingStaleTimeEnum.GetBooking,
        enabled,
      })) ?? [],
    combine: (results) => {
      return {
        data: results.map((result, i) => ({ [userIds[i]]: (result.data as { data: BookingCompressed[] })?.data })),
        isPending: results.some((result) => result.isPending),
      };
    },
  });

  return { bookings, isPending };
};

export const useSearchBookings = (
  options: ISearchBookingsParameters,
  enabled = true,
  requiredCapabilities: (keyof BookingCapabilities)[] = [],
  fetchAll = false
): {
  bookings: BookingCompressed[] | undefined;
  isLoading: boolean;
  isError: boolean;
  fetchNextPage: (options?: FetchNextPageOptions | undefined) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          data: BookingCompressed[];
          nextPage: string;
        },
        unknown
      >,
      Error
    >
  >;
  hasNextPage?: boolean;
  invalidateSearchBookings: () => void;
} => {
  const queryClient = useQueryClient();

  const invalidateSearchBookings = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [BookingQueryKeys.SearchBookings, options, requiredCapabilities],
    });
  }, [options, queryClient, requiredCapabilities]);

  if (!options.status || options.status?.length === 0) {
    options = {
      ...options,
      status: [JxtBookingStatusEnum.APPROVED, JxtBookingStatusEnum.REQUESTED, JxtBookingStatusEnum.FINISHED],
    };
  }

  const [bookings, setBookings] = useState<BookingCompressed[]>([]);
  const fetchBookings = useCallback(
    async ({ pageParam = '' }) => {
      const newOptions = { ...options };
      if (pageParam) {
        newOptions.page = pageParam;
      }

      return BookingService.search(newOptions);
    },
    [options]
  );

  const shouldFetch = enabled && options.from !== undefined && options.to !== undefined && options.from < options.to;

  const { data, isLoading, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [BookingQueryKeys.SearchBookings, options, requiredCapabilities],
    queryFn: fetchBookings,
    getNextPageParam: (lastPage: { nextPage?: string; data: BookingCompressed[] }) => {
      // must return undefined if it's the last page
      // lastPage.nextPage is ''
      return lastPage.nextPage?.length && lastPage.nextPage.length > 0 ? lastPage.nextPage : undefined;
    },
    initialPageParam: '',
    refetchInterval: 30000,
    enabled: shouldFetch,
  });

  useEffect(() => {
    if (data?.pages) {
      const pages = [...data.pages];
      const flatBookings = uniqBy(flatMap(pages.map((page) => page.data)), 'id');

      if (requiredCapabilities.length === 0) {
        setBookings(flatBookings);
      } else {
        const filteredBookings = flatBookings.filter((booking) =>
          requiredCapabilities.some((capability) => booking.capabilities?.[capability])
        );
        setBookings(filteredBookings);
      }

      if (hasNextPage && fetchAll) {
        fetchNextPage();
      }
    }
  }, [data, hasNextPage, fetchNextPage, fetchAll]);

  return { bookings, isLoading, isError, fetchNextPage, hasNextPage, invalidateSearchBookings };
};

// This method exists because the API has a limit of 44 days per request.
export const useSplittedBookingSearch = (options: ISearchBookingsParameters, enabled = true) => {
  const [bookings, setBookings] = useState<BookingCompressed[]>([]);
  const nbOfDays = useMemo(() => {
    return options?.to && options?.from ? Math.ceil(options.to.diff(options.from, 'days').days) : 0;
  }, [options]);

  const numberApiRequests = useMemo(() => {
    return Math.ceil(nbOfDays / MAX_BATCH_DAYS);
  }, [nbOfDays]);

  const splittedOptions = useMemo(() => {
    const start = options.from;
    const end = options.to;
    const res: ISearchBookingsParameters[] = [];
    for (let i = 0; i < numberApiRequests; i++) {
      const from = start?.plus({ days: i * MAX_BATCH_DAYS });
      const to =
        from && end
          ? from.plus({ days: MAX_BATCH_DAYS }) > end
            ? end
            : from.plus({ days: MAX_BATCH_DAYS })
          : undefined;

      const requestOptions = {
        ...options,
        from,
        to,
      };

      res.push(requestOptions);
    }

    return res;
  }, [options, numberApiRequests]);

  const { data: responses, isPending } = useQueries({
    queries: splittedOptions.map((o: ISearchBookingsParameters) => {
      return {
        queryKey: [BookingQueryKeys.GetBooking, o],
        queryFn: () => BookingService.search(o),
        staleTime: BookingStaleTimeEnum.GetBooking,
        enabled,
      };
    }),
    combine: (results) => {
      return {
        data: results
          .map((result) => result.data)
          .filter((r): r is { data: BookingCompressed[]; nextPage: string } => !!r),
        isPending: results.some((result) => result.isPending),
      };
    },
  });

  useEffect(() => {
    const innerBookings: BookingCompressed[] = [];
    const fetchRecursive = async (r: {
      data: BookingCompressed[];
      nextPage: string;
    }): Promise<{ data: BookingCompressed[]; nextPage: string }> => {
      innerBookings.push(...r.data);

      if (r.nextPage) {
        return await BookingService.search({ page: r.nextPage }).then(fetchRecursive);
      }

      return r;
    };

    responses?.forEach(async (r) => {
      await fetchRecursive(r);
    });

    setBookings(innerBookings);
  }, [responses]);

  return { bookings, isPending };
};

export const useFetchExistingBookings = (
  deskOrParkingResourceTypes: TDeskOrParkingResourceTypeDto[],
  range?: IRange,
  user?: User | null
) => {
  const { bookings, isLoading, invalidateSearchBookings } = useSearchBookings(
    {
      ...range,
      organizerId: user?.id,
      resourceTypeId: deskOrParkingResourceTypes.map((resourceType) => resourceType.id),
      status: [JxtBookingStatusEnum.APPROVED, JxtBookingStatusEnum.REQUESTED],
    },
    true,
    ['cancel', 'update']
  );

  return { bookings, isLoading, invalidateSearchBookings };
};

export const useFetchRemainingBookingsCheck = (
  options: IRemainingBookingsCheckOptions,
  enabled = true
): { data: WorkplaceRuleRemainingDays[] | undefined; isError: boolean } => {
  const fetchRemaining = useCallback(() => BookingService.getCheckRemainingBookings(options), [options]);

  const { data, isError } = useQuery({
    queryKey: [BookingQueryKeys.GetRemainingBookingsCheck, options],
    queryFn: () => fetchRemaining(),
    staleTime: BookingStaleTimeEnum.GetRemainingBookingsCheck,
    gcTime: 0,
    enabled,
    placeholderData: keepPreviousData,
  });

  return { data, isError };
};

export const useInvalidateBookingQueries = () => {
  const queryClient = useQueryClient();

  const invalidateBookingQueries = useCallback(
    (ids: number[]) => {
      queryClient.invalidateQueries({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        predicate: (query: any): boolean => {
          return (
            query.queryKey[0] === BookingQueryKeys.GetUserBookings ||
            (query.queryKey[0] === BookingQueryKeys.GetBooking && ids.includes(query.queryKey[1])) ||
            query.queryKey[0] === BookingQueryKeys.SearchBookings
          );
        },
      });
    },
    [queryClient]
  );

  return { invalidateBookingQueries };
};
